import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { TEAM_NAMES, type AdminDashboardDto, type AdminRoomDto, type Winner } from '@mafia/core';
import { api, ApiError } from '@/lib/api';
import { PHASE_NAMES } from '@/lib/format';
import { useGame } from '@/store/game';
import { Alert, Button, Card, Empty, PageSpinner } from '@/components/ui';
import { StatTile } from '@/components/Stats';
import { ReasonModal } from './ReasonModal';

const WINNER_COLORS: Record<Winner, string> = { TOWN: '#6366f1', MAFIA: '#dc2626', MANIAC: '#a855f7', DRAW: '#71717a' };

export function AdminDashboard() {
  const qc = useQueryClient();
  const toast = useGame((s) => s.toast);
  const dash = useQuery({ queryKey: ['admin', 'dashboard'], queryFn: () => api<AdminDashboardDto>('/api/admin/dashboard'), refetchInterval: 5000 });
  const [target, setTarget] = useState<{ kind: 'room' | 'game'; room: AdminRoomDto } | null>(null);

  if (dash.isLoading) return <PageSpinner />;
  if (dash.isError) return <Alert>Не удалось загрузить дашборд</Alert>;
  const d = dash.data!;
  const totalFinished = Object.values(d.winners).reduce((a, b) => a + b, 0);

  async function confirm(reason: string) {
    if (!target) return;
    try {
      if (target.kind === 'room') await api(`/api/admin/rooms/${target.room.id}/close`, { method: 'POST', body: { reason } });
      else await api(`/api/admin/games/${target.room.game!.id}/abort`, { method: 'POST', body: { reason } });
      toast(target.kind === 'room' ? 'Комната закрыта' : 'Игра прервана', 'good');
      setTarget(null);
      await qc.invalidateQueries({ queryKey: ['admin'] });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Ошибка', 'bad');
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Онлайн" value={d.online} tone="green" />
        <StatTile label="Активные комнаты" value={d.activeRooms} />
        <StatTile label="Идут игры" value={d.activeGames} tone="gold" />
        <StatTile label="Игр сегодня" value={d.games.finishedToday} />
        <StatTile label="Пользователей" value={d.users.total} />
        <StatTile label="Новых сегодня" value={d.users.newToday} />
        <StatTile label="Заблокировано" value={d.users.banned} tone="red" />
        <StatTile label="Игр всего / прервано" value={`${d.games.finished} / ${d.games.aborted}`} />
      </div>

      <Card className="p-5">
        <h2 className="mb-3 font-display text-lg">Кто побеждает</h2>
        {totalFinished === 0 ? (
          <p className="text-sm text-zinc-500">Завершённых игр пока нет.</p>
        ) : (
          <>
            <div className="flex h-4 overflow-hidden rounded-full" role="img" aria-label="Распределение побед по командам">
              {(Object.keys(d.winners) as Winner[]).map((w) =>
                d.winners[w] ? <div key={w} style={{ width: `${(d.winners[w] / totalFinished) * 100}%`, background: WINNER_COLORS[w] }} /> : null,
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              {(Object.keys(d.winners) as Winner[]).map((w) => (
                <span key={w} className="flex items-center gap-2 text-zinc-400">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: WINNER_COLORS[w] }} />
                  {TEAM_NAMES[w]}: <b className="text-zinc-100">{d.winners[w]}</b> ({Math.round((d.winners[w] / totalFinished) * 100)}%)
                </span>
              ))}
            </div>
          </>
        )}
      </Card>

      <Card className="overflow-hidden">
        <h2 className="px-5 pt-5 font-display text-lg">Активные комнаты</h2>
        {d.rooms.length === 0 ? (
          <Empty title="Сейчас нет открытых комнат" />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base mt-3">
              <thead>
                <tr>
                  <th>Комната</th>
                  <th>Хост</th>
                  <th>Игроки</th>
                  <th>Состояние</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.rooms.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="flex items-center gap-1.5">
                        {r.isPrivate && <Lock className="h-3.5 w-3.5 text-gold-400" />}
                        {r.name}
                        <span className="font-mono text-xs text-zinc-600">{r.code}</span>
                      </span>
                    </td>
                    <td className="text-zinc-400">{r.hostNickname}</td>
                    <td>
                      {r.members}/{r.maxPlayers}
                    </td>
                    <td className="text-zinc-400">
                      {r.game ? `${PHASE_NAMES[r.game.phase as keyof typeof PHASE_NAMES] ?? r.game.phase}, день ${r.game.day}, живы ${r.game.alive}/${r.game.players}` : 'Лобби'}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {r.game && (
                        <Button size="sm" variant="ghost" onClick={() => setTarget({ kind: 'game', room: r })}>
                          Прервать игру
                        </Button>
                      )}
                      <Button size="sm" variant="danger" onClick={() => setTarget({ kind: 'room', room: r })}>
                        Закрыть
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ReasonModal
        open={target !== null}
        title={target?.kind === 'room' ? `Закрыть комнату «${target.room.name}»?` : 'Прервать игру?'}
        action={target?.kind === 'room' ? 'Закрыть комнату' : 'Прервать игру'}
        onClose={() => setTarget(null)}
        onConfirm={confirm}
      />
    </div>
  );
}
